import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, TrendingUp, Download, Eye, Sparkles, Search, Layers, Flame, LayoutDashboard } from "lucide-react";
import BrainLogo from "@/components/BrainLogo";
import LegalLinks from "@/components/LegalLinks";
import RarityBadge, { rarityMeta } from "@/components/RarityBadge";
import GemIcon from "@/components/GemIcon";
import BackToTop from "@/components/BackToTop";
import ThemeToggle from "@/components/ThemeToggle";
import Seo from "@/components/Seo";
import { useAuth } from "@/lib/auth";
import { useTutorial } from "@/lib/tutorial";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "trending", label: "Trending", icon: TrendingUp },
  { key: "recent", label: "Recent", icon: Sparkles },
  { key: "popular", label: "Most imported", icon: Download },
];

export default function Explore({ embedded = false }) {
  const { user } = useAuth();
  const [packs, setPacks] = useState(null);
  const [filter, setFilter] = useState("trending");
  const [q, setQ] = useState("");

  const { startIfNew } = useTutorial();
  useEffect(() => {
    if (!embedded || !user) return;
    const t = setTimeout(() => startIfNew("explore"), 1000);
    return () => clearTimeout(t);
  }, [embedded, user?.id]);

  useEffect(() => {
    axios
      .get(`${API}/discover/packs`, { params: { sort: filter, q: q.trim() || undefined } })
      .then((r) => setPacks(r.data.packs || []))
      .catch(() => setPacks([]));
  }, [filter, q]);

  const ogImg = (slug, hasThumb) =>
    hasThumb ? `${process.env.REACT_APP_BACKEND_URL}/api/og-image/${slug}` : null;

  return (
    <div className={embedded ? "" : "min-h-screen bg-[#0A0A0B] text-[#F4F4F5] overflow-x-hidden"} data-testid="explore-page">
      <Seo title="Explore — community courses & studies"
        description="Discover and import community-made study courses and studies on Studivault — flashcards, quizzes and notes across every subject. One click to add to your library."
        path="/explore" />
      {!embedded && (
        <div
          aria-hidden
          className="fixed inset-0 pointer-events-none -z-10"
          style={{
            background:
              "radial-gradient(900px 480px at 18% 0%, rgba(124,92,255,0.16), transparent 60%), radial-gradient(700px 400px at 86% 30%, rgba(90,200,250,0.10), transparent 60%)",
          }}
        />
      )}

      {!embedded && (
        <header className="border-b border-white/5 px-4 md:px-8 py-4 sticky top-0 z-30 backdrop-blur-md bg-[#0A0A0B]/80">
          <div className="max-w-[1280px] mx-auto flex items-center justify-between gap-2">
            <Link to={user ? "/app" : "/"} className="flex items-center gap-2.5 min-w-0" data-testid="explore-home-link">
              <BrainLogo size={26} />
              <span className="font-semibold tracking-tight truncate">Studivault</span>
            </Link>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <ThemeToggle />
              {user ? (
                <Link to="/app" data-testid="explore-dashboard-btn">
                  <button className="btn btn-primary text-[13px] flex items-center gap-1.5">
                    <LayoutDashboard size={14} /> Dashboard
                  </button>
                </Link>
              ) : (
                <>
                  <Link to="/login" className="btn btn-ghost text-[13px] hidden sm:inline-flex">Sign in</Link>
                  <Link to="/signup">
                    <button className="btn btn-primary text-[13px]" data-testid="explore-signup-btn">Get started</button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </header>
      )}

      <main className={embedded ? "max-w-[1280px] mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8" : "max-w-[1280px] mx-auto px-5 md:px-8 py-12 md:py-16"}>
        {/* Hero */}
        <div className="mb-10 max-w-2xl">
          <div className="eyebrow mb-3">PUBLIC LIBRARY</div>
          <h1 className="display-2 mb-4">Courses &amp; studies from the community.</h1>
          <p className="body-lg text-[#B0AEBC]">
            Pre-made courses and studies — decks, quizzes and notes published by the community.
            One click to import into your own space.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-9">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, topic, tag…"
              className="w-full bg-black/50 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-[13.5px] focus:outline-none focus:border-[#7C5CFF] transition-colors"
              data-testid="explore-search-input"
            />
          </div>
          <div className="flex items-center gap-1.5 bg-white/[0.04] border border-white/10 rounded-full p-1">
            {FILTERS.map((f) => {
              const Icon = f.icon;
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`explore-filter-${f.key}`}
                  className={`relative inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors ${
                    active ? "text-white" : "text-[#8E8B99] hover:text-white"
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="explore-pill"
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(135deg,#7C5CFF55,#5AC8FA33)",
                        boxShadow: "0 0 18px rgba(124,92,255,0.4)",
                      }}
                      transition={{ type: "spring", stiffness: 320, damping: 26 }}
                    />
                  )}
                  <Icon size={12} className="relative" />
                  <span className="relative">{f.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Grid */}
        {packs === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="surface rounded-2xl h-[280px] shimmer" />
            ))}
          </div>
        ) : packs.length === 0 ? (
          <div className="surface rounded-2xl p-12 text-center" data-testid="explore-empty">
            <Layers size={28} className="mx-auto text-[#71717A] mb-4" />
            <p className="text-[15px] text-[#B0AEBC] mb-1">No courses or studies yet for this filter.</p>
            <p className="text-[12.5px] text-[#71717A]">
              Be the first — publish a study from your library to seed the public feed.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {packs.map((p, i) => {
              const img = ogImg(p.slug, !!p.has_thumbnail);
              const meta = rarityMeta(p.rarity);
              const boosted = p.boost_until && new Date(p.boost_until).getTime() > Date.now();
              const isFree = !p.price_gems || p.price_gems === 0;
              return (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.32, delay: Math.min(i * 0.03, 0.25) }}
                >
                  <Link
                    to={`/packs/${p.slug}`}
                    data-testid={`explore-pack-${p.slug}`}
                    className="group block surface rounded-2xl overflow-hidden hover:border-[#7C5CFF]/30 transition-all relative"
                    style={{
                      transition: "transform 200ms, box-shadow 200ms, border-color 200ms",
                      boxShadow: boosted
                        ? `0 0 32px -10px ${meta.glow}, 0 0 0 1px ${meta.color}55`
                        : `0 0 24px -16px ${meta.glow}`,
                      borderColor: `${meta.color}22`,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 18px 40px -12px ${meta.glow}, 0 0 0 1px ${meta.color}66`)}
                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = boosted
                      ? `0 0 32px -10px ${meta.glow}, 0 0 0 1px ${meta.color}55`
                      : `0 0 24px -16px ${meta.glow}`)}
                  >
                    {/* Top-left rarity badge */}
                    <div className="absolute top-2.5 left-2.5 z-10 flex flex-col items-start gap-1">
                      <RarityBadge rarity={p.rarity} size="xs" />
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[8.5px] font-bold uppercase tracking-[0.14em] border ${p.is_course ? "bg-[#5AC8FA]/15 border-[#5AC8FA]/45 text-[#5AC8FA]" : "bg-white/10 border-white/20 text-[#D8D6E0]"}`}>
                        {p.is_course ? "Course" : "Study"}
                      </span>
                    </div>
                    {/* Top-right price badge */}
                    <div className="absolute top-2.5 right-2.5 z-10">
                      {isFree ? (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-[0.14em] bg-[#34D399]/15 border border-[#34D399]/40 text-[#34D399]">FREE</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold tabular-nums bg-[#7C5CFF]/12 border border-[#7C5CFF]/40 text-[#7C5CFF]">
                          <GemIcon size={9} glow={false} /> {p.price_gems}
                        </span>
                      )}
                    </div>
                    {boosted && (
                      <div className="absolute top-10 right-2.5 z-10">
                        <motion.span
                          animate={{ opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 1.6, repeat: Infinity }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-[0.14em] bg-[#FCA5A5]/15 border border-[#FCA5A5]/50 text-[#FCA5A5]"
                        >
                          <Flame size={9} /> Boosted
                        </motion.span>
                      </div>
                    )}

                    {/* Thumbnail */}
                    <div className="aspect-square w-full relative bg-[#0E0E11] overflow-hidden">
                      {img ? (
                        <img
                          src={img}
                          alt={p.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-center px-6">
                          <div
                            className="w-14 h-14 rounded-full mb-4 flex items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, ${meta.color}, #7C5CFF)`,
                              boxShadow: `0 0 28px ${meta.glow}`,
                            }}
                          >
                            <Sparkles size={20} className="text-white" />
                          </div>
                          <div className="text-[15px] font-semibold leading-snug text-white line-clamp-3">{p.title}</div>
                        </div>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="p-4">
                      <div className="text-[14.5px] font-semibold text-white line-clamp-2 mb-2 group-hover:text-[#B8A4FF] transition-colors">
                        {p.title}
                      </div>
                      <div className="text-[12px] text-[#8E8B99] mb-3 line-clamp-2 min-h-[32px]">
                        {p.description || p.summary_brief || "An AI-crafted study pack."}
                      </div>
                      <div className="flex items-center justify-between text-[11.5px] text-[#8E8B99]">
                        <span className="truncate max-w-[120px]">@{(p.owner_name || "anon").split(" ")[0]}</span>
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center gap-1"><Eye size={11} /> {p.views || 0}</span>
                          <span className="inline-flex items-center gap-1"><Download size={11} /> {p.imports || 0}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        {packs && packs.length > 0 && (
          <div className="mt-16 surface rounded-2xl p-7 md:p-9 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <div className="text-[18px] font-semibold mb-1.5">Want to publish your own?</div>
              <div className="text-[13px] text-[#B0AEBC]">
                Generate a study, then hit Share — Pro members get a public, importable link.
              </div>
            </div>
            <Link to="/signup">
              <button className="btn btn-primary px-6 py-3 text-[14px]" data-testid="explore-cta-signup">
                Start free <ArrowRight size={14} />
              </button>
            </Link>
          </div>
        )}
      </main>

      {!embedded && (
        <footer className="border-t border-white/5 px-5 md:px-8 py-8 mt-10">
          <div className="max-w-[1280px] mx-auto flex flex-col md:flex-row items-center justify-between gap-2 text-[12px] text-[#8E8B99]">
            <span>© 2026 Studivault</span>
            <LegalLinks>
              <Link to="/" className="hover:text-white flex items-center gap-1"><ArrowLeft size={11} /> Home</Link>
            </LegalLinks>
          </div>
        </footer>
      )}
      <BackToTop />
    </div>
  );
}
